import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PhoneCall, Check, ExternalLink, Sparkles, X, PartyPopper } from "lucide-react";
import { STAGES, SELLER_STAGES, priorityCls, fmtDate } from "./shared";
import LeadDrawer from "./LeadDrawer";

const TASK_POLL_MS = 25000;

function PipelineAiBanner({ onOpenAssistant }) {
    const [sug, setSug] = useState(null);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        api.get("/crm/pipeline/ai-suggestions").then((r) => setSug(r.data)).catch(() => {});
    }, []);

    if (dismissed || !sug) return null;

    const items = [
        sug.follow_up_count > 0 && { key: "followup", label: `Contatta ${sug.follow_up_count} lead da ricontattare`, tone: "border-red-400/40 text-red-200", dataTone: "red" },
        sug.hot_leads_count > 0 && { key: "hot", label: `${sug.hot_leads_count} lead cald${sug.hot_leads_count === 1 ? "o" : "i"} da seguire oggi`, tone: "border-amber-400/40 text-amber-200", dataTone: "amber" },
        sug.trattative_ferme_count > 0 && { key: "ferme", label: `${sug.trattative_ferme_count} trattativ${sug.trattative_ferme_count === 1 ? "a ferma" : "e ferme"}`, tone: "border-orange-400/40 text-orange-200", dataTone: "orange" },
        sug.incarichi_count > 0 && { key: "incarichi", label: `${sug.incarichi_count} incaric${sug.incarichi_count === 1 ? "o" : "hi"} in scadenza`, tone: "border-purple-400/40 text-purple-200", dataTone: "purple" },
        sug.match_alerts_count > 0 && { key: "match", label: `${sug.match_alerts_count} match immobile-lead da verificare`, tone: "border-blue-400/40 text-blue-200", dataTone: "blue" },
        sug.senza_agente > 0 && { key: "unassigned", label: `${sug.senza_agente} lead senza agente`, tone: "border-white/20 text-ink-200", dataTone: "gray" },
    ].filter(Boolean);

    if (items.length === 0) return null;

    return (
        <div data-testid="pipeline-ai-banner" className="mb-4 flex items-center gap-3 flex-wrap px-4 py-3 border border-gold/25 bg-gold/5">
            <Sparkles size={15} className="text-gold shrink-0" />
            <span className="text-[11px] text-ink-300 shrink-0">L'AI suggerisce:</span>
            <div className="flex items-center gap-2 flex-wrap flex-1">
                {items.slice(0, 4).map((it) => (
                    <span key={it.key} data-testid={`pipeline-ai-suggestion-${it.key}`} data-tone={it.dataTone}
                        className={`vd-ai-pill text-[11px] px-2.5 py-1 border bg-black/10 ${it.tone}`}>{it.label}</span>
                ))}
            </div>
            <button data-testid="pipeline-ai-open-assistant" onClick={onOpenAssistant}
                className="text-[11px] text-gold hover:underline shrink-0 whitespace-nowrap">Apri assistente →</button>
            <button data-testid="pipeline-ai-dismiss" onClick={() => setDismissed(true)}
                className="text-ink-500 hover:text-ink-200 shrink-0"><X size={13} /></button>
        </div>
    );
}

export default function CrmPipeline({ onOpenAiAssistant }) {
    const [columns, setColumns] = useState(null);
    const [stages, setStages] = useState(STAGES);
    const [kind, setKind] = useState("buyer");
    const [dragging, setDragging] = useState(null);
    const [overCol, setOverCol] = useState(null);
    const [openLead, setOpenLead] = useState(null);
    const [agents, setAgents] = useState([]);
    const [tasks, setTasks] = useState([]);

    const load = async (k = kind) => {
        try {
            const [p, a] = await Promise.all([api.get("/crm/pipeline", { params: { kind: k } }), api.get("/crm/agents")]);
            setColumns(p.data.columns);
            setStages((k === "seller" ? SELLER_STAGES : STAGES));
            setAgents(a.data.agents || []);
        } catch (e) { console.error(e); }
    };

    const loadTasks = useCallback(async () => {
        try {
            const r = await api.get("/crm/tasks");
            setTasks(r.data.tasks || []);
        } catch (e) { console.error(e); }
    }, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(kind); }, [kind]);

    useEffect(() => {
        loadTasks();
        const t = setInterval(loadTasks, TASK_POLL_MS);
        return () => clearInterval(t);
    }, [loadTasks]);

    const completeTask = async (id) => {
        setTasks((cur) => cur.filter((t) => t.id !== id));
        try { await api.post(`/crm/tasks/${id}/complete`); toast.success("Task completata"); }
        catch { toast.error("Errore completamento task"); loadTasks(); }
    };

    const onDrop = async (stageId) => {
        setOverCol(null);
        if (!dragging || dragging.stage === stageId) { setDragging(null); return; }
        const lead = dragging;
        setDragging(null);
        // optimistic move
        setColumns((cols) => {
            const next = {};
            for (const [k, v] of Object.entries(cols)) next[k] = v.filter((x) => x.id !== lead.id);
            next[stageId] = [{ ...lead, stage: stageId }, ...next[stageId]];
            return next;
        });
        try { await api.put(`/crm/leads/${lead.id}/stage`, { stage: stageId }); }
        catch { load(); }
    };

    if (!columns) return <div className="p-8 text-center text-ink-400 text-sm">Caricamento pipeline...</div>;

    return (
        <div data-testid="pipeline-tab">
            <PipelineAiBanner onOpenAssistant={onOpenAiAssistant} />
            <div className="flex items-center gap-2 mb-4">
                <button data-testid="pipeline-kind-buyer" onClick={() => setKind("buyer")}
                    className={`px-4 py-2 text-xs border ${kind === "buyer" ? "border-gold text-gold bg-gold/5" : "border-white/15 text-ink-400 hover:text-ink-100"}`}>Acquirenti</button>
                <button data-testid="pipeline-kind-seller" onClick={() => setKind("seller")}
                    className={`px-4 py-2 text-xs border ${kind === "seller" ? "border-gold text-gold bg-gold/5" : "border-white/15 text-ink-400 hover:text-ink-100"}`}>Venditori (acquisizione)</button>
            </div>
            <p className="text-xs text-ink-400 mb-5">Trascina le card tra le colonne per aggiornare la fase. Ogni spostamento viene registrato nella timeline del lead. La colonna "Nuovi Task" raccoglie tutte le attività da chiudere, generate da qualsiasi sezione del CRM (aggiornata automaticamente).</p>
            <div className="flex gap-3 overflow-x-auto pb-4" style={{ minHeight: 420 }}>
                <div data-testid="pipeline-col-tasks" className="flex-shrink-0 w-64 border border-white/10 border-t-2 border-t-amber-400 bg-ink-900/30">
                    <div className="px-3 py-2.5 flex items-center justify-between border-b border-white/10">
                        <div className="flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                            <span className="text-[10px] uppercase tracking-wider text-ink-100">Nuovi Task / Da chiamare</span>
                        </div>
                        <span className="text-[10px] text-ink-400">{tasks.length}</span>
                    </div>
                    <div className="p-2 space-y-3 min-h-[100px] max-h-[420px] overflow-y-auto">
                        {tasks.length === 0 && <div className="text-[11px] text-ink-500 text-center py-6">Nessuna task aperta</div>}
                        {tasks.map((t) => (
                            <div key={t.id} data-testid={`pipeline-task-${t.id}`}
                                onClick={() => t.lead_id && setOpenLead(t.lead_id)}
                                className={`vd-card vd-lead-row p-3 ${t.lead_id ? "cursor-pointer" : ""}`}>
                                <div className="flex items-start justify-between gap-2">
                                    <span className="text-sm text-ink-50 font-medium leading-snug">{t.title}</span>
                                    <button data-testid={`pipeline-task-complete-${t.id}`} title="Segna come completata"
                                        onClick={(e) => { e.stopPropagation(); completeTask(t.id); }}
                                        className="shrink-0 p-1 border border-white/15 text-ink-300 hover:text-green-300 hover:border-green-400/50"><Check size={12} /></button>
                                </div>
                                <div className="flex items-center justify-between mt-2">
                                    <span className="text-[10px] text-ink-500">{t.due_at ? fmtDate(t.due_at).split(",")[0] : "senza scadenza"}</span>
                                    {t.listing_url && (
                                        <a href={t.listing_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                                            data-testid={`pipeline-task-call-${t.id}`} title="Apri annuncio per il numero"
                                            className="text-ink-300 hover:text-gold flex items-center gap-1"><PhoneCall size={11} /><ExternalLink size={10} /></a>
                                    )}
                                </div>
                                {t.agent && <div className="text-[10px] text-purple-200 mt-1.5">👤 {t.agent}</div>}
                            </div>
                        ))}
                    </div>
                </div>
                {stages.map((s) => (
                    <div key={s.id} data-testid={`pipeline-col-${s.id}`}
                        onDragOver={(e) => { e.preventDefault(); setOverCol(s.id); }}
                        onDragLeave={() => setOverCol((c) => (c === s.id ? null : c))}
                        onDrop={() => onDrop(s.id)}
                        className={`flex-shrink-0 w-64 border border-white/10 border-t-2 ${s.accent} bg-ink-900/30 transition-colors ${overCol === s.id ? "bg-white/5 border-gold/40" : ""}`}>
                        <div className="px-3 py-2.5 flex items-center justify-between border-b border-white/10">
                            <div className="flex items-center gap-2">
                                <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
                                <span className="text-[10px] uppercase tracking-wider text-ink-100">{s.label}</span>
                                {s.id === "venduto" && <PartyPopper size={12} className="text-emerald-400" />}
                            </div>
                            <span className="text-[10px] text-ink-400">{(columns[s.id] || []).length}</span>
                        </div>
                        <div className="p-2 space-y-3 min-h-[100px]">
                            {(columns[s.id] || []).map((l) => (
                                <div key={l.id} data-testid={`pipeline-card-${l.id}`} draggable
                                    onDragStart={() => setDragging(l)} onDragEnd={() => setDragging(null)}
                                    onClick={() => setOpenLead(l.id)}
                                    className={`vd-card vd-lead-row p-3 cursor-grab active:cursor-grabbing transition-colors ${dragging?.id === l.id ? "opacity-40" : ""} ${s.id === "venduto" ? "ring-1 ring-emerald-500/30" : ""}`}>
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-sm text-ink-50 font-medium truncate">{l.name}</span>
                                        {s.id === "venduto"
                                            ? <span data-testid={`pipeline-sold-badge-${l.id}`} className="text-[8px] uppercase px-1.5 py-0.5 bg-emerald-900/30 text-emerald-300 border border-emerald-500/40 shrink-0 flex items-center gap-1"><PartyPopper size={9} /> Venduto</span>
                                            : l.type === "client" && <span className="text-[8px] uppercase px-1 py-0.5 bg-green-900/30 text-green-300 shrink-0">Cliente</span>}
                                    </div>
                                    {l.property_title && <div className="text-[11px] text-ink-400 truncate mt-1">{l.property_title}</div>}
                                    <div className="flex items-center justify-between mt-2">
                                        <span className={`text-[9px] uppercase px-1.5 py-0.5 border ${priorityCls(l.priority)}`}>{l.priority}</span>
                                        <span className="text-[10px] text-ink-500">{fmtDate(l.last_activity_at).split(",")[0]}</span>
                                    </div>
                                    {l.agent && <div className="text-[10px] text-purple-200 mt-1.5">👤 {l.agent}</div>}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
            {openLead && <LeadDrawer leadId={openLead} agents={agents} onClose={() => setOpenLead(null)} onChanged={load} />}
        </div>
    );
}
